/**
 * Custom React Query hook for trading-related data.
 *
 * Wraps `useQuery` with sensible defaults for data that should stay fresh
 * while the user is viewing the dashboard or any trading page:
 *   - Polls every 30 seconds (`refetchInterval`)
 *   - Instantly refreshes when the browser tab regains focus (`refetchOnWindowFocus`)
 *
 * Individual queries can override either default (e.g. circuit breaker uses 10 s).
 *
 * Usage:
 *   import { useTradingQuery } from '@/hooks/useTradingQuery';
 *
 *   const { data } = useTradingQuery({
 *     queryKey: ['tradeAggregates', 'open'],
 *     queryFn: () => tradingService.getOpenAggregates(),
 *   });
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, QueryKey } from '@tanstack/react-query';

/** Default polling interval for all trading queries (ms). */
export const TRADING_REFRESH_INTERVAL = 30_000; // 30 seconds

/**
 * A thin wrapper around `useQuery` that adds auto-refresh defaults
 * suitable for trading / dashboard data.
 *
 * All standard `useQuery` options are supported and will override the
 * defaults when provided explicitly.
 */
export function useTradingQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  return useQuery<TQueryFnData, TError, TData, TQueryKey>({
    refetchInterval: TRADING_REFRESH_INTERVAL,
    refetchOnWindowFocus: true,
    ...options,
  });
}

